export interface IAdvancedSearchTabsDataResponse {
  workProductItemsDetails: IWorkProductItemsDetail[];
}

export interface IWorkProductItemsDetail {
  id: string;
  parent?: string;
  type?: string;
  text?: string;
  aAttr?: any;
  isGroup?: boolean;
  isRange?: boolean;
  isList?: boolean;
}

export interface IOperatorData {
  operatorCode: string;
  operatorLabel: string;
  seqNo: string;
}

export interface ISearchUser {
  key: number;
  value: string;
}

export interface ISearchUniqueDocIds {
  indexDocCount: number;
  SavedSearchLimit?: number;
  savedSearchLimit?: number;
}

export interface GetNestedSearchesParams {
  /** Comma-separated search or group ids, e.g. "101,202,303" */
  contextId: string;
  contextType: string;
  searchTypeCode: string;
}

export interface NestedChildSearchItem {
  searchID: number;
  searchName: string;
  lastExecutedOn: string;
  searchGroupName?: string;
}

export interface PendingSearchResponse {
  pendingSearches: string;
  error_Code: string;
}

export interface AddSearchMessageToQueueRequest {
  setId: string;
  index: number;
  advancedSearchID: number;
  searchDetails: SearchRequest;
}

export interface GetNestedSearchesResponse {
  searchSetGUID: string;
  /** May be returned as a single object from older APIs; prefer array. */
  pendingSearchDetails: PendingSearchResponse[] | PendingSearchResponse | null;
  /** When true with shouldProceed, show nested search popup (small/large). */
  shouldShowPopup?: boolean;
  ShouldShowPopup?: boolean;
  /** When true, continue with searchSetGUID / next API calls after /nested-searches or /runsearch. */
  shouldProceed?: boolean;
  ShouldProceed?: boolean;
  searchesNotExecuted: NestedChildSearchItem[];
  /** Session /runsearch: payload for POST /add-to-search-queue on Proceed */
  addSearchMessageToQueueRequest?: AddSearchMessageToQueueRequest | null;
}

export interface ISearchUniqueCountRequest {
  advSearchID: number;
  resultType: string;
  isSavedSearch: boolean;
}

export interface ISearchUniqueCountResponse {
  message: string;
  data: number;
}

export interface ISearchValidationRequest {
  searchCriteria: string;
}

export interface ISearchValidationResponse {
  searchStatus: boolean;
  error: string;
}

export interface IContentMDValidationResponse {
  error: string;
  status: boolean;
  msgs: string[];
  canContinue: boolean;
}

// Highlight regex details
export interface HighlightRegex {
  sourceValue: string;
  regexOnly: string;
  regexComplete: string;
  tokenReference: number;
  replaced: boolean;
  searchID: number;
}

// Allowed operators
export type Operator = 'AND' | 'OR' | 'NOT';

// Allowed advanced search types
export type AdvancedSearchTypeEnum =
  | 'ANALYTICS'
  | 'WORKPRODUCT'
  | 'CONCEPTUAL'
  | 'CONTENTMETADATA';

// Allowed search types
export type SearchTypeEnum = 'ADVANCED' | 'BASIC' | 'AUDIO';

// Saved search status values
export const SavedSearchStatus = {
  COMPLETED: 'COMPLETED',
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  NOTFOUND: 'NOTFOUND',
} as const;
export type SavedSearchStatusEnum =
  (typeof SavedSearchStatus)[keyof typeof SavedSearchStatus];

// Advanced search type criteria
export interface AdvancedSearchType {
  sequenceNo: number;
  operator: Operator;
  criteriaDB: string;
  criteriaBackground: string;
  caatConceptScore: number;
  audioOperator: string;
  audioLanguage: string;
  audioFileLocation: string;
  audioThreshold: number;
  timeBasisMS: number;
  advancedSearchType: AdvancedSearchTypeEnum;
  persistentSearchHitTerm: string;
  highlightRegex: HighlightRegex[];
}

// Advanced search options
export interface AdvancedSearchOptions {
  includeSanitationFilter: boolean;
  includeEmailDocuments: boolean;
  includeFamilyMember: boolean;
  includeNearDupe: boolean;
}

// Dragged tile details
export interface DraggedTile {
  searchId: number;
  tileType: string;
}

// Session search list item
export interface SessionSearchItem {
  searchLabel: string;
  createdBy: number;
  searchBatchID: number;
  searchSessionId: string;
  projectId: number;
  searchType: SearchTypeEnum;
  isPinned: boolean;
  searchID: number;
  editSavedSearchID: number;
  originalSearchQuery: string;
  searchTitle: string;
  searchStatus: string;
  draggedTiles: DraggedTile[];
  advancedSearchOptions: AdvancedSearchOptions;
  advancedSearchTypes: AdvancedSearchType[];
  searchLabelText: string;
  retainConfidenceScore: boolean;
}

// Full search request schema
export interface SearchRequest {
  projectID: number;
  projectGUID: string;
  audioIndexGuid: string;
  securityGroupID: number;
  customIndexID: number;
  advancedSearchTypes: AdvancedSearchType[];
  searchType: SearchTypeEnum;
  advSessionSearchID: string;
  advancedSearchOptions: AdvancedSearchOptions;
  advSearchID: number;
  sessionID: string;
  sessionSearchList: SessionSearchItem[];
  searchSetGUID?: string;
  executionMaxLevel?: number;
  contextIds?: string[];
  contextType?: string;
  searchTypeCode?: string;
  childSearchIDs?: string;
}

export interface SearchResponse {
  advancedSearchId: number;
  sessionSearchList: SessionSearchItem[];
}

export interface SearchResultRequest {
  searchId: number;
  resultType: string;
  isElasticSearch: number;
  isSavedSearch: boolean;
  securityGroup: number;
  sessionSearchList: SessionSearchItem[];
  sessionSearchId: string;
}

export interface SearchHitDocument {
  documentId: number;
  custodianName: string;
  masterDate: string;
  fileName: string;
  fileSize: string;
  fileType: string;
  rowNumber: number;
  docId: string;
}

export interface searchResult {
  pureHitStatus: string;
  emailThreadStatus: string;
  familyMemberStatus: string;
  nearDuplicateStatus: string;
  conceptualStatus: string;
  ConceptualResultInShoppingCart?: boolean;
  pureHit: IPureHitInfo;
  emailThread: IEmailThreadInfo;
  familyMember: IFamilyMemberInfo;
  nearDuplicate: INearDuplicateInfo;
}

export interface IPureHitInfo {
  pureHitCount: string;
  pureHitData: SearchHitDocument[];
  addedToShoppingCart: boolean;
}

export interface IEmailThreadInfo {
  emailThreadCount: string;
  emailThreadData: SearchHitDocument[];
  addedToShoppingCart: boolean;
}

export interface IFamilyMemberInfo {
  familyMemberCount: string;
  familyMemberData: SearchHitDocument[];
  addedToShoppingCart: boolean;
}

export interface INearDuplicateInfo {
  nearDuplicateCount: string;
  nearDuplicateData: SearchHitDocument[];
  addedToShoppingCart: boolean;
}

export interface ReloadSearchResultResponse {
  isCompleted: boolean;
  pureHitStatus: string;
  emailThreadStatus: string;
  familyMemberStatus: string;
  nearDuplicateStatus: string;
  conceptualStatus: string;
  pureHitCount: string;
  emailThreadCount: string;
  familyMemberCount: string;
  nearDuplicateCount: string;
  pureHitData: SearchHitDocument[];
  emailThreadData: SearchHitDocument[];
  familyMemberData: SearchHitDocument[];
  nearDuplicateData: SearchHitDocument[];
  executedOn: string;
  sessionSearchList: SessionSearchItem[];
}

export interface IRunConceptualSearchResponse {
  searchId: number;
  message?: string;
  status: boolean;
}

export interface IConceptualSearchStatusRequest {
  searchId: number;
  resultType: string;
  isElasticSearch: number;
  isSavedSearch: boolean;
}

export interface IConceptualSearchStatusResponse {
  cnt: string;
  status: string;
}

export interface ISaveRequest {
  searchID: number;
  searchGroupID: number;
  sessionSearchID: number;
  sessionID: string;
  searchLabel: string;
  searchType: SearchTypeEnum;
  selectedSecurityGropuId: number;
  retainConfidenceScore: boolean;
  projectID: number;
  projectGUID: string;
  advSessionSearchID: string;
  securityGroupID: number;
  advancedSearchTypes: AdvancedSearchType[];
  advancedSearchOptions: AdvancedSearchOptions;
  sessionSearchList: SessionSearchItem[];
  IndexSecurityGroupIds: number[];
  SearchGroupSecurityGroupID?: number;
  customIndexID?: number;
}

export interface ISaveResponse {
  projectId: number;
  roleID: number;
  userID: number;
  securityGroupID: number;
  statusMessage: string;
  searchLabel: string;
  searchID: number;
  sessionSearchID: number;
  searchGroupID: number;
  retainConfidenceScore: boolean;
  selectedSecurityGropuId: number;
}

export interface ISearchStatusResponse {
  searchStatus: number;
}

export interface IValidateCriteriaRequest {
  searchCriteria: string;
  customIndexID: number;
}
